import React, { FC, useState, useEffect } from "react";
import { useParams } from "react-router";
import EPGAdminLayout from "../../../layouts/admin/EPGAdminLayout";
import { useEpgEventDetailOverrideById, useUpdateEpgEventDetailOverride } from "../../../services/epg/Query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPen, faArrowLeft, faXmark, faFloppyDisk,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router";
import { EpgEvent, EpgEventDetailOverride } from "../../../models/epg/Models";
import ListInputField from "./components/details/ListInputField";
import ImageUploadField from "./components/details/ImageUploadField";
import TextInputField from "./components/details/TextInputField";
import MultiTextareaField from "./components/details/MultiTextareaField";
import ObjectListInputField from "./components/details/ObjectListInputField";

const EPGGridView: FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [ isEditMode, setIsEditMode ] = useState(false);

    const { data: event, isLoading } = useEpgEventDetailOverrideById(id ?? "");
    const { mutateAsync: updateEpgEventDetailOverride } = useUpdateEpgEventDetailOverride();
    const [ epgEvent, setEpgEvent ] = useState<EpgEvent | undefined>(event);

    useEffect(() => {
        if (!epgEvent) {
            setEpgEvent(event);
        }
    }, [ isLoading, event, epgEvent ]);

    if (!epgEvent) {
        return (
            <EPGAdminLayout heading = "EPG Event Details">
                <p className={`ml-4`}>Error loading event details</p>
            </EPGAdminLayout>
        );
    }

    const epgEventDetail = epgEvent.detail;
    const epgEventDetailOverride = epgEvent.detailOverride;

    const goBack = () => {
        navigate(`/dashboard/epg/grid/`);
    };

    const toggleEdit = () => {
        if (isEditMode && event){
            setEpgEvent({
                ...event,
            });
        }
        setIsEditMode(!isEditMode);
    };

    const save =  async () => {
        if (!id || !epgEventDetailOverride)
            return;

        await updateEpgEventDetailOverride({ id, epgEventDetailOverride: epgEventDetailOverride });
        setIsEditMode(false);
    };

    const updateField = <K extends keyof EpgEventDetailOverride>(key: K, value: EpgEventDetailOverride[K]) => {
        if (epgEventDetailOverride) {
            setEpgEvent({
                ...epgEvent,
                detailOverride : {
                    ...epgEventDetailOverride,
                    [key]: value,
                },
            });
        }
    };

    function getNoneEmptyValue<T extends string | undefined | unknown[]>(eventDetailOverrideValue: T, eventDetailValue: T): T {
        if (!eventDetailOverrideValue)
            return eventDetailValue;

        return eventDetailOverrideValue?.length ? eventDetailOverrideValue : eventDetailValue;
    }

    return (
        <>
            <EPGAdminLayout heading="EPG Event Details">
                <div className={`px-4`}>
                    <div className={`w-3/4`}>
                        <div className="flex flex-col h-full bg-white dark:!bg-brand-700 shadow-xl dark:text-white rounded-md p-4">
                            <div className="flex flex-row items-center justify-between border-b pb-5">
                                <p className={"text-black"}>{getNoneEmptyValue(epgEventDetailOverride?.title, epgEventDetail.title)}</p>
                                <div className={"flex flex-row gap-4 mr-5"}>
                                    <button
                                        className={`flex flex-row items-center border px-2 py-1 rounded-md gap-2 bg-blue-800 dark:bg-indigo-400 ${isEditMode ? "block" : "hidden"}`}
                                        onClick={save}
                                    >
                                        <FontAwesomeIcon icon={faFloppyDisk} size="1x" fixedWidth className={`text-white`}/>
                                        <p className={`font-bold text-white`}>{"Save"}</p>
                                    </button>
                                    <button
                                        onClick={toggleEdit}
                                        className={`flex flex-row items-center border px-2 py-1 rounded-md gap-2 ${isEditMode ? "bg-red-400" : "bg-blue-800 dark:bg-indigo-400"}`}
                                    >
                                        <FontAwesomeIcon icon={isEditMode ? faXmark : faPen} size="1x" fixedWidth className={"text-white"} />
                                        <p className={`font-bold text-white`}>{isEditMode ? "Cancel" : "Edit"}</p>
                                    </button>
                                    <button onClick={goBack}>
                                        <FontAwesomeIcon icon={faArrowLeft} size="1x" fixedWidth />
                                    </button>
                                </div>
                            </div>
                            <div className="py-5 grid grid-cols-2 gap-y-8">
                                <TextInputField
                                    label="Title"
                                    value={getNoneEmptyValue(epgEventDetailOverride?.title, epgEventDetail.title) ?? ""}
                                    isEditMode={isEditMode}
                                    fieldName={"title"}
                                    updateField={updateField}
                                />
                                <TextInputField
                                    label="Subtitle"
                                    value={getNoneEmptyValue(epgEventDetailOverride?.subTitle, epgEventDetail.subTitle) ?? ""}
                                    isEditMode={isEditMode}
                                    fieldName={"subTitle"}
                                    updateField={updateField}
                                />
                                <div className={`col-span-2`}>
                                    <ObjectListInputField
                                        label={`Parental Ratings`}
                                        items={getNoneEmptyValue(epgEventDetailOverride?.parentalRatings, epgEventDetail.parentalRatings) ?? []}
                                        isEditMode={isEditMode} fieldName={`parentalRatings`}
                                        updateField={updateField}
                                    />
                                </div>
                                <div className={`col-span-2`}>
                                    <MultiTextareaField
                                        label={"Synopses"}
                                        items={getNoneEmptyValue(epgEventDetailOverride?.synopses, epgEventDetail.synopses) ?? []}
                                        isEditMode={isEditMode} fieldName={"synopses"}
                                        updateField={updateField}
                                    />
                                </div>
                                <div className={`col-span-2`}>
                                    <ListInputField
                                        label={"Categories"}
                                        items={getNoneEmptyValue(epgEventDetailOverride?.categories, epgEventDetail.categories) ?? []}
                                        isEditMode={isEditMode} fieldName="categories"
                                        updateField={updateField}
                                    />
                                </div>
                                <div className={`col-span-2`}>
                                    <ListInputField
                                        label={"Keywords"}
                                        items={getNoneEmptyValue(epgEventDetailOverride?.keywords, epgEventDetail.keywords) ?? []}
                                        isEditMode={isEditMode}
                                        fieldName={"keywords"} updateField={updateField}
                                    />
                                </div>
                                <div className={`col-span-2`}>
                                    <ImageUploadField
                                        images={getNoneEmptyValue(epgEventDetailOverride?.images, epgEventDetail.images) ?? []}
                                        isEditMode={isEditMode}
                                        title={epgEventDetailOverride?.title ?? epgEventDetail.title}
                                        updateField={updateField}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </EPGAdminLayout>
        </>
    );
};

export default EPGGridView;